import { useCallback, useEffect, useMemo, useState } from "react";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { API } from "aws-amplify";
import { useAppDispatch } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { useDataReload } from "hooks/useDataReload";
import { hideSpinner, showSpinner } from "store/reducers/spinnerReducer";
import { useStorageState } from "./useStorageState";
import { useAuthenticator } from "auth/useAuthenticator";

export const useDataGrid = ({
  url,
  reloadIntervalInSeconds = 0,
  defaultSorter = {
    name: "id",
    order: "desc",
  },
  defaultLimit = 25,
  additionalParams = null,
  gridConfigIndex,
  onRefresh = null,
  restApi = "restApi",
  highlightRowName = undefined,
  highlightRowValue = undefined,
}) => {
  const { handleAuthError } = useAuthenticator();
  const dispatch = useAppDispatch();
  const { createApiCall } = useCreateApiCall();
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [search, setSearch] = useStorageState({
    key: `${gridConfigIndex}-search`,
    type: "string",
    defaultState: "",
  });
  const [sorter, setSorter] = useState(defaultSorter);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(
    () =>
      JSON.parse(
        window.localStorage.getItem(`${gridConfigIndex}-defaultLimit`)
      ) || defaultLimit
  );
  const [totalCount, setTotalCount] = useState(0);
  const [rows, setRows] = useState([]);
  const [visibility, setVisibilityState] = useState(
    () => JSON.parse(window.localStorage.getItem(gridConfigIndex)) || []
  );

  const setVisibility = useCallback(
    (value) => {
      setVisibilityState(value);
      window.localStorage.setItem(gridConfigIndex, JSON.stringify(value));
    },
    [gridConfigIndex]
  );

  const totalPages = useMemo(
    () => Math.ceil(totalCount / limit),
    [totalCount, limit]
  );

  const getApiRequestParams = useCallback(
    () =>
      additionalParams
        ? {
            search: search.trim(),
            sorter,
            from: limit * (currentPage - 1),
            limit,
            ...additionalParams,
          }
        : {
            search: search.trim(),
            sorter,
            from: limit * (currentPage - 1),
            limit,
          },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search, sorter, limit, currentPage, additionalParams]
  );

  const getHandleDownload = useCallback(
    (columns) => async () => {
      dispatch(showSpinner());
      try {
        const result = await API.post(restApi, url, {
          body: { ...getApiRequestParams(), forCsv: true },
        });

        if (result?.error) {
          dispatch(showError(result.error));
          return;
        }

        const data = result?.records || [];
        if (!data.length) return;

        const names = columns.map((col) => col.field);
        const csvContent = [
          names.map((name) => `"${name}"`).join(","),
          ...data.map((row) => names.map((name) => `"${row[name]}"`).join(",")),
        ].join("\r\n");

        const downloadUrl = window.URL.createObjectURL(new Blob([csvContent]));
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", `${gridConfigIndex.slice(37)}.csv`);
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        dispatch(showError(error.message || "An error occurred"));
      } finally {
        dispatch(hideSpinner());
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getApiRequestParams, restApi]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadData = useCallback(
    createApiCall(
      async () => {
        typeof onRefresh === "function" && onRefresh();
        try {
          const result = await API.post(restApi, url, {
            body: getApiRequestParams(),
          });
          if (result?.error) {
            dispatch(showError(result.error));
            return;
          }
          setRows(result?.records || []);
          setTotalCount(result?.count || 0);
        } catch (error) {
          handleAuthError(error);
        } finally {
          setIsFirstLoad(false);
        }
      },
      (error) => {
        console.error(error);
        dispatch(showError("Something went wrong"));
      },
    ),
    [getApiRequestParams, restApi]
  );

  const handleSort = useCallback((field) => {
    setSorter((prevSorter) => ({
      name: field,
      order:
        prevSorter.name === field && prevSorter.order === "asc"
          ? "desc"
          : "asc",
    }));
    setCurrentPage(1);
  }, []);

  const handleChangeSearch = useCallback(
    (e) => {
      setSearch(e?.target?.value || "");
      setCurrentPage(1);
    },
    [setSearch]
  );

  const clearSearch = useCallback(() => setSearch(""), [setSearch]);

  const handleChangeRecordsPerPage = useCallback(
    (e) => {
      const value = Number(e?.target?.value || defaultLimit);
      setLimit(value);
      window.localStorage.setItem(
        `${gridConfigIndex}-defaultLimit`,
        String(value)
      );
      setCurrentPage(1);
    },
    [defaultLimit, gridConfigIndex]
  );

  const handleChangePage = useCallback((_, value) => setCurrentPage(value), []);

  useDataReload({
    loadData,
    skipFirstLoad: true,
    reloadIntervalInSeconds,
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadData();
    }, 200);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadData]);

  return {
    search,
    setSearch,
    handleChangeSearch,
    clearSearch,
    sorter,
    setSorter,
    handleSort,
    currentPage,
    totalPages,
    handleChangePage,
    handleChangeRecordsPerPage,
    setCurrentPage,
    limit,
    setLimit,
    getApiRequestParams,
    rows,
    loadData,
    totalCount,
    visibility,
    setVisibility,
    defaultLimit,
    getHandleDownload,
    isFirstLoad,
    highlightRowName,
    highlightRowValue,
  };
};
